module Gfr {
    /**
     * Systems of measurement
     */
    export enum Units {
        /**
         * The Conventional system of measurement
         */
        Conventional,

        /**
         * The SI system of measurement
         */
        SI
    }

    /**
     * Calculation types for adult GFR values (i.e., the equation to use for calculation)
     */
    export enum Type {
        /**
         * Calculation using the Modification of Diet in Renal Disease (MDRD) Study equation
         */
        Mdrd,

        /**
         * Calculation using the Chronic Kidney Disease Epidemiology Collaboration (CKD-EPI) equation
         */
        CkdEpi
    }

    export abstract class Calculator {
        /**
         * The unit of measure for serum creatinine (Scr) values, based on the calculator's system of measurement
         */
        readonly scrUnits: string;

        /** The unit of measure for the glomerular filtration rate (GFR) result */
        readonly resultUnits: string;

        /**
         * Constructs a new abstract glomerular filtration rate (GFR) calculator instance
         * @class Gfr.Calculator
         * @classdesc Abstract base class for a glomerular filtration rate (GFR) calculator
         * @abstract
         * @param {Gfr.Units} units The system used for units of measure
         * @param {number} maxVal The maximum value for the result; set to <= 0 to disable maximum value; default: 0
         */
        constructor(readonly units: Units, readonly maxVal: number = 0) {
            this.scrUnits = units == Units.SI ? "µmol/L" : "mg/dL";
            this.resultUnits = "mL/min/1.73 m²";
        }

        /**
         * Validates that the GFR result is within bounds and returns the result to display
         * @param {number} result The GFR result value to validate
         * @returns {string} A string representing the GFR calculation result
         */
        protected validate(result: number): string {
            // Set result to 0 if infinity or NaN
            result = isFinite(result) ? result : 0;

            // Return "Above {maxVal}" if result is over {maxVal}, or the numeric value otherwise
            return (this.maxVal > 0 && result > this.maxVal) ? `Above ${this.maxVal}` : result.toString();
        }
    }

    export class AdultCalculator extends Calculator {
        /**
         * The maximum result value recommended for reporting GFR values for adults
         */
        public static readonly recommendedMaxVal: number = 60;

        /**
         * Constructs a new glomerular filtration rate (GFR) calculator for adults age 18 or older
         * @class Gfr.AdultCalculator
         * @classdesc Glomerular filtration rate (GFR) calculator for adults age 18 or older
         * @param {Gfr.Units} units The system used for units of measure
         * @param {Gfr.Type} type The type of calculation to perform (i.e., the equation to use)
         * @param {number} maxVal The maximum value for the result; set to <= 0 to disable maximum value; default: 0
         */
        constructor(units: Units, readonly type: Type, readonly maxVal: number = 0) {
            super(units, maxVal);
        }

        /**
         * Calculates the glomerular filtration rate (GFR) for an adult age 18 or older with the specified
         * serum creatinine (Scr) level, age, race, and gender
         * @param {number} scr The serum creatinine (Scr) level in the units indicated by the scrUnits property
         * @param {number} age The age in years (method returns "N/A" if less than 18)
         * @param {boolean} isBlack True if the person is black/African American, or false otherwise
         * @param {boolean} isFemale True if the person is female, or false if the person is male
         * @returns {string} A string representing the GFR calculation result for display
         */
        calculate(scr: number, age: number, isBlack: boolean, isFemale: boolean): string {
            if (age < 18) {
                return "N/A";
            }

            let result: number;
            const isSi: boolean = this.units == Units.SI;

            if (this.type == Type.Mdrd) {
                // MDRD for adults
                const race: number = isBlack ? 1.212 : 1;
                const gender: number = isFemale ? 0.742 : 1;

                scr = isSi ? scr / 88.4 : scr;

                result = Math.round(175 * Math.pow(scr, -1.154) * Math.pow(age, -0.203) * race * gender);
            } else if (this.type == Type.CkdEpi) {
                // CKD-EPI for adults
                const race = isBlack ? 1.159 : 1;
                const gender = isFemale ? 1.018 : 1;
                const alpha = isFemale ? -0.329 : -0.411;

                let k: number;

                if (isSi) {
                    // SI units
                    k = isFemale ? 61.9 : 79.6;
                } else {
                    // conventional units
                    k = isFemale ? 0.7 : 0.9;
                }

                const resultMin = Math.pow(Math.min(scr / k, 1), alpha);
                const resultMax = Math.pow(Math.max(scr / k, 1), -1.209);

                result = Math.round(141 * resultMin * resultMax * Math.pow(0.993, age) * race * gender);
            }
            else {
                return "Invalid calculator type";
            }

            return super.validate(result);
        }
    }

    export class ChildCalculator extends Calculator {
        /**
         * The unit of measure for height values
         */
        public static readonly heightUnits: string = "cm";

        /**
         * The maximum result value recommended for reporting GFR values for children
         */
        public static readonly recommendedMaxVal: number = 75;

        /**
         * Constructs a new glomerular filtration rate (GFR) calculator for children under age 18
         * @class Gfr.ChildCalculator
         * @classdesc Glomerular filtration rate (GFR) calculator for children under age 18
         * @param {Gfr.Units} units The system used for units of measure
         * @param {number} maxVal The maximum value for the result; set to <= 0 to disable maximum value; default: 0
         */
        constructor(units: Units, readonly maxVal: number = 0) {
            super(units, maxVal);
        }

        /**
         * Calculates the glomerular filtration rate (GFR) for a child with the specified
         * serum creatinine (Scr) level and height
         * @param {number} scr The serum creatinine (Scr) level in the units indicated by the scrUnits property
         * @param {number} height The child's height in centimeters (cm)
         * @returns {string} A string representing the GFR calculation result for display
         */
        calculate(scr: number, height: number): string {
            const isSi: boolean = this.units == Units.SI;
            const k: number = isSi ? 36.2 : 0.41;

            let result: number = Math.round(k * height / scr);

            return super.validate(result);
        }
    }
}
